// React hooks for the HonoApiClient
// Provides React-friendly interfaces with loading states, error handling, and caching

import { useCallback, useEffect, useState } from 'react'
import type {
	CreateTaskRequest,
	Document,
	GetTasksRequest,
	PaginationMeta,
	Task,
	UpdateDocumentContentRequest,
	UpdateDocumentStatusRequest,
	WFMPatchRequest,
	WFMSearchResult,
} from '../../shared/types'
import type {
	DailyStats,
	DocumentTypeStatistics,
	StatisticsSummary,
	UserDetailedStats,
	UserStats,
} from '../lib/honoApiClient'
import { useAuthenticatedApi } from './useAuthenticatedApi'
import { useCompany } from '../contexts/CompanyContext'

/**
 * Base hook interface for async operations
 */
interface AsyncHookState<T> {
	data: T | null
	loading: boolean
	error: string | null
}

/**
 * Hook state interface for paginated data
 */
interface PaginatedHookState<T> extends AsyncHookState<T> {
	pagination: PaginationMeta | null
}

/**
 * Hook for fetching tasks with pagination and filtering support
 */
export function useTasks(params?: GetTasksRequest) {
	const apiClient = useAuthenticatedApi()
	const { selectedCompany } = useCompany()
	const [state, setState] = useState<PaginatedHookState<Task[]>>({
		data: null,
		loading: !!selectedCompany, // Only show loading if we have a company selected
		error: null,
		pagination: null,
	})

	const refetch = useCallback(
		async (newParams?: GetTasksRequest) => {
			// Don't make API call if no company is selected
			if (!selectedCompany) {
				console.log('[useTasks] Skipping API call - no company selected')
				return
			}

			setState((prev) => ({ ...prev, loading: true, error: null }))

			try {
				// Use newParams if provided, otherwise use the current params
				const requestParams = newParams || params
				const response = await apiClient.tasks.getTasks(requestParams)
				if (response.success && response.data) {
					setState({
						data: response.data,
						loading: false,
						error: null,
						pagination: response.pagination || null,
					})
				} else {
					setState({
						data: null,
						loading: false,
						error: response.error || 'Failed to fetch tasks',
						pagination: null,
					})
				}
			} catch (error) {
				setState({
					data: null,
					loading: false,
					error: error instanceof Error ? error.message : 'Unknown error',
					pagination: null,
				})
			}
		},
		[apiClient, selectedCompany, JSON.stringify(params)]
	)

	useEffect(() => {
		// Only fetch when we have a selected company or when dependencies change
		if (selectedCompany) {
			refetch()
		}
	}, [refetch, selectedCompany])

	return { ...state, refetch }
}

/**
 * Hook for fetching a specific task by ID
 */
export function useTask(id: number | null) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<Task>>({
		data: null,
		loading: false,
		error: null,
	})

	const refetch = useCallback(async () => {
		if (!id) return

		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.tasks.getTask(id)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({ data: null, loading: false, error: response.error || 'Failed to fetch task' })
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [id, apiClient])

	useEffect(() => {
		if (id) {
			refetch()
		}
	}, [id, refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching documents for a specific task
 */
export function useTaskDocuments(taskId: number | null) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<Document[]>>({
		data: null,
		loading: false,
		error: null,
	})

	const refetch = useCallback(async () => {
		if (!taskId) return

		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.tasks.getTaskDocuments(taskId)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch documents',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [taskId, apiClient])

	useEffect(() => {
		if (taskId) {
			refetch()
		}
	}, [taskId, refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching a specific document by ID
 */
export function useDocument(id: number | null) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<Document>>({
		data: null,
		loading: false,
		error: null,
	})

	const refetch = useCallback(async () => {
		if (!id) return

		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.documents.getDocument(id)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch document',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [id, apiClient])

	useEffect(() => {
		if (id) {
			refetch()
		}
	}, [id, refetch])

	return { ...state, refetch }
}

/**
 * Hook for creating tasks with loading state management
 */
export function useCreateTask() {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<Task>>({
		data: null,
		loading: false,
		error: null,
	})

	const createTask = useCallback(
		async (data: CreateTaskRequest) => {
			setState({ data: null, loading: true, error: null })

			try {
				const response = await apiClient.tasks.createTask(data)
				if (response.success && response.data) {
					setState({ data: response.data, loading: false, error: null })
					return response.data
				}
				setState({ data: null, loading: false, error: response.error || 'Failed to create task' })
				throw new Error(response.error || 'Failed to create task')
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setState({ data: null, loading: false, error: errorMessage })
				throw error
			}
		},
		[apiClient]
	)

	return { ...state, createTask }
}

/**
 * Hook for updating document status with optimistic updates
 */
export function useUpdateDocumentStatus() {
	const apiClient = useAuthenticatedApi()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const updateStatus = useCallback(
		async (id: number, data: UpdateDocumentStatusRequest) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.documents.updateDocumentStatus(id, data)
				if (response.success) {
					setLoading(false)
					return true
				}
				setError(response.error || 'Failed to update document status')
				setLoading(false)
				return false
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	return { updateStatus, loading, error }
}

/**
 * Hook for updating document content
 */
export function useUpdateDocumentContent() {
	const apiClient = useAuthenticatedApi()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const updateContent = useCallback(
		async (id: number, data: UpdateDocumentContentRequest) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.documents.updateDocumentContent(id, data)
				if (response.success) {
					setLoading(false)
					return true
				}
				setError(response.error || 'Failed to update document content')
				setLoading(false)
				return false
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	return { updateContent, loading, error }
}

/**
 * Hook for file uploads with progress tracking
 */
export function useFileUpload() {
	const apiClient = useAuthenticatedApi()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const uploadFiles = useCallback(
		async (taskId: number, files: File[]) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.upload.uploadFiles(taskId, files)
				if (response.success && response.data) {
					setLoading(false)
					return response.data
				}
				setError(response.error || 'Failed to upload files')
				setLoading(false)
				throw new Error(response.error || 'Failed to upload files')
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	return { uploadFiles, loading, error }
}

/**
 * Hook for WFM search with debouncing
 */
export function useWFMSearchHono(blackCaseNumber: string, delay = 500) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<WFMSearchResult>>({
		data: null,
		loading: false,
		error: null,
	})

	const search = useCallback(
		async (searchTerm: string) => {
			if (!searchTerm.trim()) {
				setState({ data: null, loading: false, error: null })
				return
			}

			setState((prev) => ({ ...prev, loading: true, error: null }))

			try {
				const response = await apiClient.wfm.search({ blackCaseNumber: searchTerm })
				if (response.success && response.data) {
					setState({ data: response.data, loading: false, error: null })
				} else {
					setState({ data: null, loading: false, error: response.error || 'WFM search failed' })
				}
			} catch (error) {
				setState({
					data: null,
					loading: false,
					error: error instanceof Error ? error.message : 'Unknown error',
				})
			}
		},
		[apiClient]
	)

	// Debounced search effect
	useEffect(() => {
		if (!blackCaseNumber.trim()) {
			setState({ data: null, loading: false, error: null })
			return
		}

		const timeoutId = setTimeout(() => {
			search(blackCaseNumber)
		}, delay)

		return () => clearTimeout(timeoutId)
	}, [blackCaseNumber, delay, search])

	return { ...state, search }
}

/**
 * Hook for WFM task patching
 */
export function useWFMPatch() {
	const apiClient = useAuthenticatedApi()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const patchTasks = useCallback(
		async (data: WFMPatchRequest) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.wfm.patchTasks(data)
				if (response.success && response.data) {
					setLoading(false)
					return response.data
				}
				setError(response.error || 'Failed to patch WFM tasks')
				setLoading(false)
				throw new Error(response.error || 'Failed to patch WFM tasks')
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	return { patchTasks, loading, error }
}

/**
 * Hook for workflow operations
 */
export function useWorkflow() {
	const apiClient = useAuthenticatedApi()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const updateWorkflow = useCallback(
		async (taskId: number) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.tasks.updateWorkflow(taskId)
				if (response.success) {
					setLoading(false)
					return response.data
				}
				setError(response.error || 'Failed to update workflow')
				setLoading(false)
				throw new Error(response.error || 'Failed to update workflow')
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	const getWorkflowStatus = useCallback(
		async (taskId: number) => {
			setLoading(true)
			setError(null)

			try {
				const response = await apiClient.tasks.getWorkflowStatus(taskId)
				if (response.success) {
					setLoading(false)
					return response.data
				}
				setError(response.error || 'Failed to get workflow status')
				setLoading(false)
				throw new Error(response.error || 'Failed to get workflow status')
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error'
				setError(errorMessage)
				setLoading(false)
				throw error
			}
		},
		[apiClient]
	)

	return { updateWorkflow, getWorkflowStatus, loading, error }
}

/**
 * Hook for fetching statistics summary
 */
export function useStatisticsSummary(period = '30') {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<StatisticsSummary>>({
		data: null,
		loading: true,
		error: null,
	})

	const refetch = useCallback(async () => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.statistics.getSummary(period)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch statistics summary',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [apiClient, period])

	useEffect(() => {
		refetch()
	}, [refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching daily statistics with date filtering
 */
export function useDailyStatistics(params?: { from?: string; to?: string }) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<DailyStats[]>>({
		data: null,
		loading: true,
		error: null,
	})

	const refetch = useCallback(async () => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.statistics.getDailyStats(params)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch daily statistics',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [apiClient, params])

	useEffect(() => {
		refetch()
	}, [refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching document type statistics
 */
export function useDocumentTypeStatistics(params?: { from?: string; to?: string }) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<DocumentTypeStatistics>>({
		data: null,
		loading: true,
		error: null,
	})

	const refetch = useCallback(async () => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.statistics.getByDocumentType(params)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch document type statistics',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [apiClient, params])

	useEffect(() => {
		refetch()
	}, [refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching all users' statistics (admin only)
 */
export function useUsersStatistics(params?: { from?: string; to?: string; limit?: string }) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<UserStats[]>>({
		data: null,
		loading: true,
		error: null,
	})

	const refetch = useCallback(async () => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.statistics.getUsersStats(params)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch users statistics',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [apiClient, params])

	useEffect(() => {
		refetch()
	}, [refetch])

	return { ...state, refetch }
}

/**
 * Hook for fetching detailed users' statistics with document type breakdown
 */
export function useUsersDetailedStatistics(params?: {
	from?: string
	to?: string
	limit?: string
}) {
	const apiClient = useAuthenticatedApi()
	const [state, setState] = useState<AsyncHookState<UserDetailedStats[]>>({
		data: null,
		loading: true,
		error: null,
	})

	const refetch = useCallback(async () => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		try {
			const response = await apiClient.statistics.getUsersDetailedStats(params)
			if (response.success && response.data) {
				setState({ data: response.data, loading: false, error: null })
			} else {
				setState({
					data: null,
					loading: false,
					error: response.error || 'Failed to fetch detailed users statistics',
				})
			}
		} catch (error) {
			setState({
				data: null,
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			})
		}
	}, [apiClient, params])

	useEffect(() => {
		refetch()
	}, [refetch])

	return { ...state, refetch }
}
